// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Menu / Product page  (route /menu/:id)
// One catch on its own page: credited photo, name + scientific name, tags, description, per-kg price, pack-size rows with
// steppers (500 g / 1 kg), the cut list (one cut per catch, prep fee shown), related catches, and a sticky bar that
// Adds / Updates / Removes the catch in the cart. Editing state is the shared hooks/useCatchDraft (same as the pack
// sheet), so both screens always agree on totals. Opened from a card's photo/name, the site search and the cart's "Edit cut".
// Deliberately NOT here (nothing in the system supports it): ratings/reviews, landing-time and cold-chain/sub-zero/
// formalin-free claims, net-weight or glaze figures, "In Stock", savings/strike-through prices, custom-tailoring
// preferences, "perfect pairing". The favourite heart (signed-in only) saves the catch with its cut + pack (hooks/useFavouriteCatches).
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, Minus, Plus, Share2, Trash2 } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import { cutsFor, prepLabel } from "../data/cuts";
import { PACK_SIZES, pricePer100g, type PackId } from "../data/packs";
import { seafoodItems } from "../data/seafood";
import useCart from "../hooks/useCart";
import useCatchDraft, { MAX_PACKS } from "../hooks/useCatchDraft";
import useDocumentTitle from "../hooks/useDocumentTitle";
import useFavouriteCatches from "../hooks/useFavouriteCatches";
import FavouriteHeart from "../components/menu/FavouriteHeart";
import { formatInr } from "../utils/priceFormat";
import { getWikimediaSrcSet } from "../utils/wikimediaImage";
import NotFound from "./NotFound";

export default function ProductPage() {
  const { id } = useParams();
  const item = seafoodItems.find((candidate) => candidate.id === id) ?? null;
  const navigate = useNavigate();
  const { qtyOf, setQty, cutIdOf } = useCart();
  const favourites = useFavouriteCatches();
  const [note, setNote] = useState("");

  const inCart: Record<PackId, number> = item ? { "500g": qtyOf(item.id, "500g"), "1kg": qtyOf(item.id, "1kg") } : { "500g": 0, "1kg": 0 };
  const { draft, cut, setCutId, alreadyInCart, unit, total, summary, empty, set } = useCatchDraft(item, inCart, item ? cutIdOf(item.id) : undefined);
  useDocumentTitle(item ? item.name : "Catch not found");

  if (!item) return <NotFound />;

  const cuts = cutsFor(item);
  const related = seafoodItems.filter((other) => other.category === item.category && other.id !== item.id).slice(0, 3);

  const apply = () => {
    for (const pack of PACK_SIZES) setQty(item.id, pack.id, draft[pack.id], cut?.id);
  };
  const remove = () => {
    for (const pack of PACK_SIZES) setQty(item.id, pack.id, 0);
    navigate("/menu");
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: item.name, url });
      else {
        await navigator.clipboard.writeText(url);
        setNote("Link copied");
      }
    } catch {
      // The customer dismissed the share sheet, or clipboard access was refused — nothing to report.
    }
  };

  const barLabel = alreadyInCart ? (empty ? "Remove from cart" : "Update cart") : "Add to cart";

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-32 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs dynamicLabel={item.name} />

        <div className="mx-auto max-w-5xl px-4 pt-2">
          <div className="grid gap-6 md:grid-cols-2">
            <figure className="relative self-start overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
              <img
                src={item.image}
                srcSet={getWikimediaSrcSet(item.image)}
                sizes="(min-width: 768px) 50vw, 100vw"
                alt={item.name}
                width={800}
                height={600}
                className="aspect-[4/3] w-full object-cover"
              />
              {item.tags.length > 0 && (
                <ul aria-label="Tags" className="absolute left-3 top-3 flex flex-wrap gap-1">
                  {item.tags.map((tag) => (
                    <li key={tag} className="rounded-full bg-white/95 px-2 py-0.5 text-[11px] font-bold text-ocean-900 shadow-sm">
                      {tag}
                    </li>
                  ))}
                </ul>
              )}
              <figcaption className="px-3 py-2 text-xs text-ocean-800/75 dark:text-foam-100/70">Photo: {item.credit}</figcaption>
            </figure>

            <div className="flex flex-col gap-5">
              <header>
                <div className="flex items-start justify-between gap-3">
                  <h1 className="font-display text-3xl font-bold text-ocean-950 dark:text-foam-50 sm:text-4xl">{item.name}</h1>
                  <div className="flex shrink-0 gap-2">
                  {favourites.available && (
                    <FavouriteHeart
                      name={item.name}
                      saved={favourites.has(item.id)}
                      // Saving from here remembers the chosen cut and pack, so "Add to cart" on the Favourites page matches.
                      onToggle={() => void favourites.toggle(item.id, { cut: cut?.id ?? null, pack: PACK_SIZES.find((pack) => draft[pack.id] > 0)?.id ?? null })}
                    />
                  )}
                  <button
                    type="button"
                    onClick={share}
                    aria-label={`Share ${item.name}`}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10"
                  >
                    <Share2 className="h-5 w-5" aria-hidden="true" />
                  </button>
                  </div>
                </div>
                <p className="mt-1 text-sm italic text-ocean-800/80 dark:text-foam-100/75">{item.scientificName}</p>
                <p role="status" className="sr-only">{note}</p>
                {note && <p aria-hidden="true" className="mt-1 text-xs font-semibold text-teal-700 dark:text-teal-300">{note}</p>}
                <p className="mt-3 text-ocean-800/90 dark:text-foam-100/85">{item.description}</p>
                <p className="mt-3">
                  <span className="font-display text-3xl font-bold text-ocean-950 dark:text-foam-50">{item.price}</span>
                  <span className="ml-1 text-sm text-ocean-800/70 dark:text-foam-100/65">{item.unit}</span>
                  <span className="ml-2 text-xs text-ocean-800/70 dark:text-foam-100/65">(₹{pricePer100g(item).toFixed(0)} per 100 g)</span>
                </p>
              </header>

              <section aria-labelledby="pack-heading">
                <h2 id="pack-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Choose pack size</h2>
                <ul className="mt-2 space-y-2">
                  {PACK_SIZES.map((pack) => {
                    const qty = draft[pack.id];
                    return (
                      <li
                        key={pack.id}
                        className={`flex items-center justify-between gap-3 rounded-2xl bg-white p-3 ring-1 dark:bg-ocean-900 ${qty > 0 ? "ring-2 ring-teal-600" : "ring-ocean-900/10 dark:ring-white/10"}`}
                      >
                        <div>
                          <p className="font-bold text-ocean-950 dark:text-foam-50">{pack.label}</p>
                          <p className="text-sm text-ocean-800/80 dark:text-foam-100/75">{formatInr(unit(pack))} per pack</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => set(pack.id, qty - 1)}
                            disabled={qty === 0}
                            aria-label={`Fewer ${pack.label} packs`}
                            className="flex h-11 w-11 items-center justify-center rounded-full bg-ocean-700/10 text-ocean-900 hover:bg-ocean-700/15 disabled:opacity-40 dark:bg-white/10 dark:text-foam-50"
                          >
                            <Minus className="h-4 w-4" aria-hidden="true" />
                          </button>
                          <span aria-label={`${qty} of ${pack.label}`} className="w-6 text-center font-bold text-ocean-950 dark:text-foam-50">{qty}</span>
                          <button
                            type="button"
                            onClick={() => set(pack.id, qty + 1)}
                            disabled={qty >= MAX_PACKS}
                            aria-label={`More ${pack.label} packs`}
                            className="flex h-11 w-11 items-center justify-center rounded-full bg-ocean-800 text-white hover:bg-ocean-700 disabled:opacity-40"
                          >
                            <Plus className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>

              <section aria-labelledby="cut-heading">
                <h2 id="cut-heading" className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Cut &amp; cleaning</h2>
                <div role="radiogroup" aria-labelledby="cut-heading" className="mt-2 space-y-2">
                  {cuts.map((option) => {
                    const selected = cut?.id === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setCutId(option.id)}
                        className={`flex w-full items-start gap-3 rounded-2xl bg-white p-3 text-left ring-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 dark:bg-ocean-900 ${
                          selected ? "ring-2 ring-teal-600" : "ring-ocean-900/10 hover:bg-ocean-700/5 dark:ring-white/10"
                        }`}
                      >
                        <span
                          aria-hidden="true"
                          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${selected ? "border-teal-600 bg-teal-600 text-white" : "border-ocean-800/40"}`}
                        >
                          {selected && <Check className="h-3 w-3" />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-center gap-x-2">
                            <span className="font-bold text-ocean-950 dark:text-foam-50">{option.name}</span>
                            <span className="text-sm font-semibold text-teal-700 dark:text-teal-300">{prepLabel(option)}</span>
                          </span>
                          {option.tag && <span className="block text-xs font-semibold text-ocean-800/80 dark:text-foam-100/75">{option.tag}</span>}
                          <span className="mt-0.5 block text-sm text-ocean-800/80 dark:text-foam-100/75">{option.description}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            </div>
          </div>

          {related.length > 0 && (
            <section aria-labelledby="related-heading" className="mt-10">
              <h2 id="related-heading" className="font-display text-xl font-bold text-ocean-950 dark:text-foam-50">More from the {item.category.toLowerCase()} catch</h2>
              <ul className="mt-3 grid gap-3 sm:grid-cols-3">
                {related.map((other) => (
                  <li key={other.id}>
                    <Link
                      to={`/menu/${other.id}`}
                      className="flex items-center gap-3 rounded-2xl bg-white p-3 shadow-sm ring-1 ring-ocean-900/5 hover:bg-ocean-700/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 dark:bg-ocean-900 dark:ring-white/10"
                    >
                      <img src={other.image} alt="" width={64} height={64} loading="lazy" className="h-16 w-16 shrink-0 rounded-xl object-cover" />
                      <span className="min-w-0">
                        <span className="block font-bold text-ocean-950 dark:text-foam-50">{other.name}</span>
                        <span className="block text-sm text-ocean-800/80 dark:text-foam-100/75">{other.price} {other.unit}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <div className="pointer-events-none fixed inset-x-0 bottom-4 z-40 px-4 pr-20 sm:pr-4">
          <div className="pointer-events-auto mx-auto flex max-w-xl items-center justify-between gap-3 rounded-full bg-ocean-800 py-2 pl-5 pr-2 text-white shadow-lg shadow-ocean-950/30">
            <p className="min-w-0 text-sm leading-tight">
              <span className="block truncate font-bold">{empty ? (alreadyInCart ? "Nothing selected" : "Pick a pack size") : formatInr(total)}</span>
              {!empty && <span className="block truncate text-xs text-white/80">{summary}{cut ? ` · ${cut.name}` : ""}</span>}
            </p>
            {alreadyInCart && empty ? (
              <button
                type="button"
                onClick={remove}
                className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-full bg-coral-500 px-5 text-sm font-bold text-white hover:bg-coral-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                {barLabel}
              </button>
            ) : (
              <button
                type="button"
                onClick={apply}
                disabled={empty}
                className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-full bg-white px-5 text-sm font-bold text-ocean-900 hover:bg-foam-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-50"
              >
                {barLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </PageTransition>
  );
}
